import React from "react";
import { useTranslation } from "react-i18next";
import { Progress } from "../../features/training/models/Progress";

interface MemberProgressPanelProps {
  progress: Progress;
}

/**
 * One enrolment's Progress: theory and practice totals, then every topic that still
 * needs units. Read-only; shared by the member page and the portal.
 */
const MemberProgressPanel: React.FC<MemberProgressPanelProps> = ({ progress }) => {
  const { t } = useTranslation();
  const totals = [
    progress.theoryRequired > 0 &&
      t("training.progress.theory", { done: progress.theoryDone, required: progress.theoryRequired }),
    progress.practicalRequired > 0 &&
      // "Besondere Ausbildungsfahrten" belongs to licence-class programmes only (PD-0023).
      t(progress.isLicenceClassProgramme ? "training.progress.practical" : "training.progress.practice", {
        done: progress.practicalDone,
        required: progress.practicalRequired,
      }),
  ].filter(Boolean);

  return (
    <div className="mt-2 rounded-lg bg-grey-95 p-3 text-sm dark:bg-primary-20" data-testid="member-progress">
      {totals.length > 0 && <p className="font-medium text-grey-20 dark:text-white">{totals.join(" · ")}</p>}
      {progress.isComplete ? (
        <p className="mt-1 text-green-700 dark:text-green-400">{t("training.progress.complete")}</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {progress.remainingTopics.map((topic) => (
            <li key={topic.topicId} className="flex justify-between gap-3 text-grey-30 dark:text-grey-90">
              <span className="min-w-0 truncate">
                {topic.code} · {topic.title}
              </span>
              <span className="shrink-0 text-grey-50 dark:text-grey-70">
                {t("training.progress.remaining", { count: topic.remainingCount })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MemberProgressPanel;
